import { BrandConfigByCode } from "./common";

// Mirrors gac-m/apps/gac-products/src/constants/astroMigration.ts (which also
// mirrors gac-m/apps/gac-products/wrangler.jsonc's route patterns). Astro uses
// these same two lists to INCLUDE only the pages it now builds; here they
// EXCLUDE pages Gatsby no longer builds. Update all three together.
// Brand WYSIWYG pages (ASTRO_MIGRATED_BRAND_PAGE_BRAND_SLUGS) are built by gac-main
// instead; keep that list in sync with gac-m/apps/gac-main/wrangler.jsonc's
// /<brandSlug>/* routes.

// Product groups migrated to Astro. Specify brandSlug + groupSlug exactly as they appear in Directus.
// Excluding a group also suppresses its /pigment-data and /technical-chart pages.
export const ASTRO_MIGRATED_PRODUCT_GROUPS: { brandSlug: string; groupSlug: string }[] = [
  { brandSlug: "golden-artist-acrylics", groupSlug: "heavy-body" },
  { brandSlug: "williamsburg-artist-oil-colors", groupSlug: "oil-colors" },
];

// Regexes tested against individual product slugs. A slug matching any entry will not be built by Gatsby.
export const ASTRO_MIGRATED_PRODUCT_SLUG_PATTERNS: RegExp[] = [/^heavy-body-/, /^williamsburg-artist-oil-colors-/];

// Brands whose WYSIWYG (old_pages) pages, e.g. /williamsburg-artist-oil-colors/history-of-williamsburg-artist-oils,
// are built by gac-main in Astro. Gatsby skips these and links to them with a real <a>.
// The brand root (/<brandSlug>) is not included and is still built by Gatsby.
export const ASTRO_MIGRATED_BRAND_PAGE_BRAND_SLUGS: string[] = ["williamsburg-artist-oil-colors"];

// Standalone tool pages under /products/ that live in Astro, not Gatsby.
const ASTRO_PRODUCT_TOOL_SLUGS = new Set(["color-converter"]);

const KNOWN_BRAND_SLUGS = new Set(Object.values(BrandConfigByCode).map((b) => b.slug));

// True for any /products/... path that Gatsby no longer builds: the group/pigment-data/
// technical-chart pages for any group in ASTRO_MIGRATED_PRODUCT_GROUPS, individual product
// pages whose slug matches ASTRO_MIGRATED_PRODUCT_SLUG_PATTERNS, and standalone Astro tool
// pages such as /products/color-converter. Links to these paths must use a real <a> instead
// of Gatsby's <Link>, since GatsbyLink would try to client-side route to a page that isn't
// built in this app.
export const isAstroProductsPath = (to: string): boolean => {
  if (!to.startsWith("/products/")) return false;

  const [, brandOrProductSlug, groupSlug] = to.split("/").filter(Boolean);
  if (!brandOrProductSlug) return false;

  // /products/<brandSlug>/<groupSlug>[/pigment-data|/technical-chart]
  if (groupSlug) {
    return ASTRO_MIGRATED_PRODUCT_GROUPS.some((g) => g.brandSlug === brandOrProductSlug && g.groupSlug === groupSlug);
  }

  if (ASTRO_PRODUCT_TOOL_SLUGS.has(brandOrProductSlug)) return true;

  // /products/<brandSlug> is a brand landing page, still built by Gatsby.
  if (KNOWN_BRAND_SLUGS.has(brandOrProductSlug)) return false;

  // /products/<productSlug> - only individual products matching the migrated patterns.
  return ASTRO_MIGRATED_PRODUCT_SLUG_PATTERNS.some((p) => p.test(brandOrProductSlug));
};

// True for /<brandSlug>/<pageSlug> paths of brands in ASTRO_MIGRATED_BRAND_PAGE_BRAND_SLUGS.
// Not the brand root, and not /products/... paths (see isAstroProductsPath).
export const isAstroBrandPagePath = (to: string): boolean => {
  const [brandSlug, pageSlug] = to.split(/[?#]/)[0].split("/").filter(Boolean);
  return !!pageSlug && brandSlug !== "products" && ASTRO_MIGRATED_BRAND_PAGE_BRAND_SLUGS.includes(brandSlug);
};

// True for any path owned by an Astro app instead of Gatsby.
export const isAstroPath = (to: string): boolean => isAstroProductsPath(to) || isAstroBrandPagePath(to);
